import { BankCodesForAll } from '@revenuemarket/bank';
import QueryString from 'qs';

import { API } from '~apis/axios/instance';
import { ResponseBaseWithData } from '~types/response';

export type MemberType = 'REPRESENTATIVE' | 'NORMAL';

interface Page {
  totalElements: number;
  totalPages: number;
  size: number;
  first: boolean;
  last: boolean;
  pageable: IPageable;
  numberOfElements: number;
  empty: boolean;
}

export interface BusinessEntity {
  id: number;
  companyId: string;
  businessRegNumber: string;
  name: string;
  alias: string;
  memo?: string;
  address?: string;
  phoneNumber?: string;
  industry?: string;
  registrationCertificateUrl: string;
  isFavorite: boolean;
  members: BusinessEntityMember[];
  last3MonthCashIn: number;
  last3MonthCashOut: number;
  numOfMemos: number;
}

export interface BusinessEntityMember {
  id: number;
  name: string;
  type: MemberType;
  position: string;
  department: string;
  phoneNumber: string;
  email: string;
  isTaxInvoiceManager: boolean;
}
interface ISort {
  empty: boolean;
  sorted: boolean;
  unsorted: boolean;
}
interface IPageable {
  offset: number;
  sort: ISort;
  pageSize: number;
  paged: boolean;
  pageNumber: number;
  unpaged: boolean;
}

export interface SimilarBusinessEntity {
  similarList: ISimilarListItem[];
  denyCreate: boolean;
}
export interface ISimilarListItem {
  id: number;
  companyId: string;
  businessRegNumber: string;
  name: string;
  alias: string;
  members: IMembersItem[];
  address: string;
  phoneNumber: string;
  industry: string;
  registrationCertificateUrl: string;
  isFavorite: boolean;
  synonyms: string[];
  contractBalanceSetting: IContractBalanceSetting;
  createdAt: string;
  updatedAt: string;
  sameNames: string[];
  businessEntityLabel: string;
}
interface IMembersItem {
  id: number;
  name: string;
  type: string;
  position: string;
  department: string;
  phoneNumber: string;
  email: string;
  isTaxInvoiceManager: boolean;
  createdAt: string;
  updatedAt: string;
}
interface IContractBalanceSetting {
  showBalance: boolean;
  openingYearMonth: string;
  openingSalesBalance: number;
  openingPurchaseBalance: number;
}

export interface BusinessEntitySimilarRequest {
  companyId: string;
  query: {
    name: string;
    businessRegNo?: string;
  };
}
export function getSimilarBusinessEntityList({ companyId, query }: BusinessEntitySimilarRequest) {
  return API.get<ResponseBaseWithData<SimilarBusinessEntity>>(
    `/api/v3/companies/${companyId}/business-entities/similar`,
    {
      params: query,
    },
  );
}

export function getBusinessEntityList({
  companyId,
  favoriteOnly,
}: {
  companyId: string;
  favoriteOnly: boolean;
}) {
  const params = QueryString.stringify(
    {
      favoriteOnly: favoriteOnly === true ? favoriteOnly : undefined,
    },
    { arrayFormat: 'repeat' },
  );

  return API.get<ResponseBaseWithData<BusinessEntity[]>>(
    `/api/v2/companies/${companyId}/business-entities?${params}`,
  );
}

export interface BusinessEntityDetailFromNICE extends Page {
  content: {
    companyInfo?: ICompanyInfo;
    companyName: string;
    existing: boolean;
  }[];
}
interface ICompanyInfo {
  aa22NameAndCode: string;
  amnisuyn: string;
  btpNo: string;
  businessRegNo: string;
  bzdNo: string;
  crpNo: string;
  employeesNumber: string;
  engAddress: string;
  engName: string;
  engRepresentative: string;
  eprDataStatusDivCode: string;
  eprMdyDivCode: string;
  financeDivCode: string;
  kisCode: string;
  korAddress: string;
  korName: string;
  korRepresentative: string;
  ltgMarketDivCode: string;
  lotEngAddress: string;
  lotKorAddress: string;
  openingDate: string;
  shortName: string;
  industryName: string;
  industryCode: string;
  scaleDivCode: string;
  stockCode: string;
  tel: string;
}
export function searchBusinessEntity({
  companyId,
  searchParam,
}: {
  companyId: string;
  searchParam: string;
}) {
  return API.get<ResponseBaseWithData<BusinessEntity[]>>(
    `/api/v2/companies/${companyId}/business-entities/search?searchParam=${searchParam}`,
  );
}

export interface BusinessEntityBankAccount {
  id?: number;
  companyId: string;
  organizationCode: BankCodesForAll;
  businessEntityId: number;
  alias: string;
  accountNumber: string;
  accountHolder: string | null;
  identified?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export enum BusinessEntityType {
  CORPORATION = 'CORPORATION',
  NATIONAL_INDIVIDUAL = 'NATIONAL_INDIVIDUAL',
  FOREIGN_INDIVIDUAL = 'FOREIGN_INDIVIDUAL',
  UNKNOWN = 'UNKNOWN',
}
export interface CreateBusinessEntityRequest {
  companyId: string;
  name: string;
  alias?: string;
  memo?: string;
  businessRegNumber: string | null;
  representativeNames: string[];
  address?: string;
  btpNo?: string;
  bzdNo?: string;
  phoneNumber?: string;
  industry?: string;
  taxInvoiceManagers?: { name: string; email: string }[];
  bankAccounts: BusinessEntityBankAccount[];
  type?: BusinessEntityType;
}

export function createBusinessEntity({
  companyId,
  name,
  alias,
  memo,
  businessRegNumber,
  representativeNames,
  address,
  btpNo,
  bzdNo,
  phoneNumber,
  industry,
  taxInvoiceManagers,
  bankAccounts,
  type,
}: CreateBusinessEntityRequest) {
  return API.post<ResponseBaseWithData<{ id: number }>>(
    `/api/v2/companies/${companyId}/business-entities`,
    {
      data: {
        name,
        alias,
        memo,
        businessRegNumber,
        representativeNames,
        address,
        btpNo,
        bzdNo,
        phoneNumber,
        industry,
        taxInvoiceManagers,
        bankAccounts,
        type,
      },
    },
  );
}

export const contractsRefresh = ({ companyId }: { companyId: string }) => {
  return API.post<ResponseBaseWithData>(
    `/api/v2/companies/${companyId}/business-entities/contracts/refresh`,
  );
};
